import React, { useRef, useState } from "react";
import { Box, Button } from "grommet";
import { FormPrevious } from "grommet-icons";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import useAutomationFlows from "../useAutomationFlows";
import AutomationFlowEditor, { flowName } from "./AutomationFlowEditor";
import { presentToastErrorContent, presentToastSuccess } from "../../shared/Toast";
import "../SettingsAutomationFlows.css";

export default function MessagingCheckinFlow({ hotel, children, fetchHotel }) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { state, busy, error, change } = useAutomationFlows(hotel._id);
  const [settingsState, setSettingsState] = useState({ ready: false, dirty: false, bodies: {} });
  const [saving, setSaving] = useState(false);
  const settingsRef = useRef(null);
  const savedRevisions = useRef({});
  const [flowDirty, setFlowDirty] = useState(false);
  const id = new URLSearchParams(location.search).get("flow");
  const flow = state?.flows.find(item => item.id === id) || (!id ? state?.flows.find(item => item.kind === "precheckin") : null);
  const back = () => navigate("/settings/automation-flows");
  return <main className="automation-overview automation-editor-page">
    {!state && !error && <p role="status">{t("automation.loading")}</p>}
    {state && !flow && <Box gap="medium" pad="medium">
      <p>{t("automation.noSpecial")}</p>
      <Button label={t("automation.back")} onClick={back} />
    </Box>}
    {flow && <>
      <Button plain className="automation-back" aria-label={t("automation.back")} icon={<FormPrevious size="small" aria-hidden="true" />} type="button" label={t("automation.back")} disabled={busy || saving} onClick={() => {
        if (!(flowDirty || settingsState.dirty) || window.confirm(t("automation.discard"))) back();
      }} />
      <header className="automation-page-header"><h1>{flowName(flow, t)}</h1></header>
      <AutomationFlowEditor key={flow.id} flow={flow} flows={state.flows} timezone={state.timezone} busy={busy || saving || !settingsState.ready}
        extraDirty={settingsState.dirty} extraBodies={settingsState.bodies} saveDisabled={!settingsState.ready || settingsState.invalid}
        onClose={back} onDirty={setFlowDirty} onSave={async (config, revision) => {
          setSaving(true);
          try {
            // Validate/persist the schedule first; failed settings remain editable and retryable.
            const result = await change({ action: "edit", id: flow.id, revision: savedRevisions.current[flow.id] ?? revision, config });
            if (!result) return null;
            savedRevisions.current[flow.id] = result.flows?.find(item => item.id === flow.id)?.revision ?? revision + 1;
            await settingsRef.current.save();
            await fetchHotel();
            if (result.notice) presentToastSuccess(t(`automation.notices.${result.notice}`));
            back();
            return result;
          } catch (error) { presentToastErrorContent(error); return null; }
          finally { setSaving(false); }
        }}>
        {config => React.cloneElement(children, { key: flow.id, automationKind: flow.kind, channels: config.channels,
          onDraftState: setSettingsState, settingsRef })}
      </AutomationFlowEditor>
    </>}
  </main>;
}
